/**
 * Migrate World on Wealth: funded, received, net, burn, pipeline. Business capital, never
 * investment. Expected deal shares are shown apart and never counted as received.
 */
import { useState, type ReactNode } from 'react';
import { useApp } from './context';
import { useAsync } from './hooks';
import { Dismiss, Empty, ErrorNote, Modal, Panel, Stat } from './ui';
import { asTransactions } from '../lib/sofar';
import { MW_CATEGORIES, mwSummary, type Ccy, type MwCategory, type MwData, type MwDeal, type MwStage, type MwSummary } from '../lib/mw';
import { DASH, money, naira, nairaShort } from '../lib/format';
import { errorMessage, type Repo } from '../data/repo';
import type { Base } from './context';

const monthName = (k: string) => new Date(`${k}-01T00:00:00`).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
const STAGE: Record<MwStage, string> = { lead: 'Lead', committed: 'Committed', closed: 'Closed, not paid', paid: 'Paid', lost: 'Lost' };
const today = () => new Date().toISOString().slice(0, 10);

function share(d: MwDeal) {
  if (d.expected_share === null) return DASH;
  return d.share_max && d.share_max !== d.expected_share
    ? `${money(d.expected_share, d.currency)} to ${money(d.share_max, d.currency)}` : money(d.expected_share, d.currency);
}

function headline(s: MwSummary): string {
  const since = s.since ? monthName(s.since) : '';
  if (s.received <= 0) return `You've funded ${nairaShort(s.funded)} since ${since}. Nothing has come back yet.`;
  if (s.net < 0) return `${nairaShort(s.received)} has come back. ${nairaShort(-s.net)} is still to recover.`;
  return `Migrate World has returned ${nairaShort(s.net)} more than you funded.`;
}

/** Migrate World data and summary, as the Wealth card and the close pack both show it. Null when not set up. */
export async function loadMw(repo: Repo, base: Base, today?: Date) {
  const data = await repo.mw();
  if (!data) return null;
  const cut = data.settings?.cockpit_from;
  const statements = cut ? await repo.mwCockpit(cut) : [];
  // This month's alerts filed as Migrate World, until the statement replaces them.
  const open = base.openPlan;
  let alerts: typeof statements = [];
  if (cut && open) {
    const rows = (await repo.provisional(open.month_start)) ?? [];
    const live = rows.filter((r) => r.status === 'provisional');
    if (live.length) {
      const rules = await repo.payeeRules();
      alerts = asTransactions(live, base.categories, rules)
        .filter((t) => t.direction === 'debit' && t.category_name === 'Migrate World')
        .map((t) => ({ tx_date: t.tx_date, amount: t.amount, currency: t.currency, description: t.description, from: 'alert' as const }));
    }
  }
  return { data, summary: mwSummary(data, [...statements, ...alerts], today), alerts: alerts.length };
}

export function MigrateWorldPanel() {
  const { repo, base } = useApp();
  const [form, setForm] = useState<null | 'cost' | 'payout' | 'deal' | 'rates'>(null);
  const state = useAsync(() => loadMw(repo, base), [base.openPlan?.id, base.categories.length]);

  if (state.error) return <Panel title="Migrate World"><ErrorNote message={state.error} onRetry={state.reload} /></Panel>;
  if (!state.data) return state.data === null
    ? <Panel title="Migrate World" meta="Business, not investment"><Empty>Not set up in this database yet.</Empty></Panel>
    : <Panel title="Migrate World"><Empty>Reading…</Empty></Panel>;

  const { data, summary: s, alerts } = state.data;
  const st = data.settings;
  const maxMonth = Math.max(1, ...s.months.map((m) => m.ngn));
  const openKey = base.openPlan ? base.openPlan.month_start.slice(0, 7) : null;
  const close = (saved: boolean) => { setForm(null); if (saved) state.reload(); };

  return (
    <Panel title="Migrate World" meta={<span className="tag tag-watch">Business, not investment</span>}>
      <p className="mw-head">{s.since ? headline(s) : 'No Migrate World costs recorded yet.'}</p>
      <div className="stats-3">
        <Stat label="You funded" value={nairaShort(s.funded)} foot={s.since ? `since ${monthName(s.since)}` : 'Nothing recorded'} />
        <Stat label="Profit share received" value={nairaShort(s.received)} foot={s.receivedAtPlanningRate ? 'part at planning rate' : 'logged payouts'} />
        <Stat label={s.net < 0 ? 'Still to recover' : 'Ahead by'} value={nairaShort(Math.abs(s.net))} foot="received minus funded" />
      </div>

      <ul className="ledger mw-lines">
        <li><span>Monthly cost<small>{s.burnMonths.length ? `average of ${s.burnMonths.map(monthName).join(', ')} (months with Migrate World costs)` : 'needs a complete month'}</small></span><b>{s.burn === null ? DASH : naira(s.burn)}</b></li>
        {s.monthsPerDeal !== null && (
          <li><span>One committed deal covers<small>average committed share at planning rate</small></span><b>{s.monthsPerDeal.toFixed(1)} months</b></li>
        )}
        {s.breakEvenDeals !== null && s.net < 0 && (
          <li><span>Deals to break even<small>committed shares needed to recover what you funded</small></span><b>{s.breakEvenDeals}</b></li>
        )}
      </ul>

      <h4 className="mw-sub">By month</h4>
      <ul className="mw-bars">
        {s.months.map((m) => (
          <li key={m.month}>
            <span>{monthName(m.month)}</span>
            <i><b style={{ width: `${(m.ngn / maxMonth) * 100}%` }} /></i>
            <em>{m.ngn ? nairaShort(m.ngn) : '₦0'}{m.month === openKey ? ' so far' : ''}</em>
          </li>
        ))}
      </ul>
      <p className="mw-note">Before {st ? monthName(st.cockpit_from.slice(0, 7)) : DASH}: your Migrate World Ledger. From then: your bank statements{alerts ? `, plus ${alerts} alert${alerts === 1 ? '' : 's'} this month` : ''}, and costs paid outside your accounts.</p>

      {s.byCategory.length > 0 && (
        <>
          <h4 className="mw-sub">By category</h4>
          <ul className="ledger">{s.byCategory.map((c) => <li key={c.category}><span>{c.category}</span><b>{naira(c.ngn)}</b></li>)}</ul>
        </>
      )}

      <h4 className="mw-sub">Pipeline <small>expected, not counted</small></h4>
      {s.pipeline.length ? (
        <ul className="ledger">
          {s.pipeline.map((d) => (
            <li key={d.id}>
              <span>{d.name}<small>{[d.program, d.option].filter(Boolean).join(' · ') || 'Program unknown'} · {STAGE[d.stage]}</small></span>
              <b>{share(d)}<small>{d.ngnLow === null ? 'no planning rate' : `≈ ${nairaShort(d.ngnLow)}${d.ngnHigh && d.ngnHigh !== d.ngnLow ? ` to ${nairaShort(d.ngnHigh)}` : ''}`}</small></b>
            </li>
          ))}
          {s.committedLow !== null && (
            <li className="ledger-total"><span>Committed, your share<small>at planning rate</small></span>
              <b>{nairaShort(s.committedLow)}{s.committedHigh && s.committedHigh !== s.committedLow ? ` to ${nairaShort(s.committedHigh)}` : ''}</b></li>
          )}
        </ul>
      ) : <Empty>No open deals.</Empty>}
      <p className="mw-note">Planning rate {st?.usd_rate ? `₦${st.usd_rate.toLocaleString('en-GB')}/$` : 'not set for $'}{st?.eur_rate ? `, ₦${st.eur_rate.toLocaleString('en-GB')}/€` : ''}. A deal counts only when its payout is logged.</p>

      <div className="mw-actions">
        <button className="secondary" onClick={() => setForm('cost')}>Log a Migrate World cost</button>
        <button className="secondary" onClick={() => setForm('payout')}>Log a payout</button>
        <button className="secondary" onClick={() => setForm('deal')}>Update a deal</button>
        <button className="link" onClick={() => setForm('rates')}>Planning rates</button>
      </div>

      {form === 'cost' && <CostForm data={data} onClose={close} />}
      {form === 'payout' && <PayoutForm data={data} onClose={close} />}
      {form === 'deal' && <DealForm data={data} onClose={close} />}
      {form === 'rates' && <RatesForm data={data} onClose={close} />}
    </Panel>
  );
}

/* ─── Forms: one row each, saved by the owner ─────────────────────────────── */

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <label className="mw-field"><span>{label}</span>{children}</label>;
}

function useSave(onClose: (saved: boolean) => void) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setErr(null);
    try { await fn(); onClose(true); } catch (e) { setErr(errorMessage(e)); setBusy(false); }
  };
  return { busy, err, run };
}

const num = (v: string) => (v.trim() === '' ? null : Number(v.replace(/,/g, '')));
const ok = (v: number | null) => v !== null && Number.isFinite(v) && v > 0;

function CostForm({ data, onClose }: { data: MwData; onClose: (saved: boolean) => void }) {
  const { repo } = useApp();
  const { busy, err, run } = useSave(onClose);
  const [f, set] = useState({ cost_date: today(), category: 'Other' as MwCategory, description: '', payee: '', currency: 'NGN' as Ccy, amount: '', rate: '', note: '' });
  const planning = f.currency === 'USD' ? data.settings?.usd_rate : f.currency === 'EUR' ? data.settings?.eur_rate : 1;
  const rate = f.currency === 'NGN' ? 1 : num(f.rate) ?? planning ?? null;
  const amount = num(f.amount);
  const ngn = amount !== null && rate ? amount * rate : null;
  const valid = f.description.trim() && ok(amount) && ok(rate);
  return (
    <Modal title="Log a Migrate World cost" kicker="Paid outside your tracked accounts" onClose={() => onClose(false)}
      footer={<><Dismiss>Cancel</Dismiss>
        <button className="cta" disabled={!valid || busy} onClick={() => run(() => repo.addMwCost({
          cost_date: f.cost_date, category: f.category, description: f.description.trim(), payee: f.payee.trim() || null,
          currency: f.currency, amount: amount!, rate_to_ngn: rate!, ngn: Math.round(ngn! * 100) / 100, note: f.note.trim() || null,
        }))}>Save</button></>}>
      <p className="mw-note">For costs not paid from your GTBank, Providus or Zenith accounts: cash, Wise, a USD wire, or someone paying for you. Bank payments with "MW" in the narration arrive on their own.</p>
      <div className="mw-form">
        <Field label="Date"><input type="date" value={f.cost_date} onChange={(e) => set({ ...f, cost_date: e.target.value })} /></Field>
        <Field label="Category"><select value={f.category} onChange={(e) => set({ ...f, category: e.target.value as MwCategory })}>{MW_CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select></Field>
        <Field label="What for"><input value={f.description} onChange={(e) => set({ ...f, description: e.target.value })} placeholder="Isaac salary (USD)" /></Field>
        <Field label="Paid to"><input value={f.payee} onChange={(e) => set({ ...f, payee: e.target.value })} /></Field>
        <Field label="Currency"><select value={f.currency} onChange={(e) => set({ ...f, currency: e.target.value as Ccy })}><option>NGN</option><option>USD</option><option>EUR</option></select></Field>
        <Field label="Amount"><input inputMode="decimal" value={f.amount} onChange={(e) => set({ ...f, amount: e.target.value })} /></Field>
        {f.currency !== 'NGN' && <Field label="Rate to ₦ (actual)"><input inputMode="decimal" value={f.rate} placeholder={planning ? String(planning) : 'required'} onChange={(e) => set({ ...f, rate: e.target.value })} /></Field>}
        <Field label="Note"><input value={f.note} onChange={(e) => set({ ...f, note: e.target.value })} /></Field>
      </div>
      <p className="mw-note">In naira: <b>{ngn === null ? DASH : naira(ngn)}</b>{f.currency !== 'NGN' && !f.rate ? ' at planning rate. Enter the actual rate if you know it.' : ''}</p>
      {err && <ErrorNote message={err} />}
    </Modal>
  );
}

function PayoutForm({ data, onClose }: { data: MwData; onClose: (saved: boolean) => void }) {
  const { repo } = useApp();
  const { busy, err, run } = useSave(onClose);
  const [f, set] = useState({ received_on: today(), deal_id: '', currency: 'USD' as Ccy, amount: '', ngn: '', account: '', note: '', markPaid: true });
  const amount = num(f.amount);
  const ngnVal = f.currency === 'NGN' ? amount : num(f.ngn);
  const valid = ok(amount) && (ngnVal === null || ok(ngnVal));
  return (
    <Modal title="Log a payout" kicker="Your profit share, once it has landed" onClose={() => onClose(false)}
      footer={<><Dismiss>Cancel</Dismiss>
        <button className="cta" disabled={!valid || busy} onClick={() => run(async () => {
          await repo.addMwPayout({ deal_id: f.deal_id || null, received_on: f.received_on, currency: f.currency, amount: amount!,
            ngn: ngnVal, account: f.account.trim() || null, note: f.note.trim() || null });
          const deal = data.deals.find((d) => d.id === f.deal_id);
          if (deal && f.markPaid) await repo.saveMwDeal({ ...deal, stage: 'paid' });
        })}>Save</button></>}>
      <div className="mw-form">
        <Field label="Received on"><input type="date" value={f.received_on} onChange={(e) => set({ ...f, received_on: e.target.value })} /></Field>
        <Field label="Deal"><select value={f.deal_id} onChange={(e) => set({ ...f, deal_id: e.target.value })}>
          <option value="">Not linked to a deal</option>{data.deals.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}</select></Field>
        <Field label="Currency"><select value={f.currency} onChange={(e) => set({ ...f, currency: e.target.value as Ccy })}><option>USD</option><option>EUR</option><option>NGN</option></select></Field>
        <Field label="Amount"><input inputMode="decimal" value={f.amount} onChange={(e) => set({ ...f, amount: e.target.value })} /></Field>
        {f.currency !== 'NGN' && <Field label="Naira value (when converted)"><input inputMode="decimal" value={f.ngn} placeholder="leave blank until converted" onChange={(e) => set({ ...f, ngn: e.target.value })} /></Field>}
        <Field label="Received into"><input value={f.account} placeholder="Wise USD" onChange={(e) => set({ ...f, account: e.target.value })} /></Field>
        <Field label="Note"><input value={f.note} onChange={(e) => set({ ...f, note: e.target.value })} /></Field>
      </div>
      {f.deal_id && <label className="remember"><input type="checkbox" checked={f.markPaid} onChange={(e) => set({ ...f, markPaid: e.target.checked })} /><span>Mark this deal as paid</span></label>}
      {err && <ErrorNote message={err} />}
    </Modal>
  );
}

function DealForm({ data, onClose }: { data: MwData; onClose: (saved: boolean) => void }) {
  const { repo } = useApp();
  const { busy, err, run } = useSave(onClose);
  const blank = { id: '', name: '', program: '', option: '', stage: 'lead' as MwStage, currency: 'USD' as Ccy, expected_share: '', share_max: '', notes: '' };
  const [f, set] = useState(blank);
  const pick = (id: string) => {
    const d = data.deals.find((x) => x.id === id);
    set(d ? { id: d.id, name: d.name, program: d.program ?? '', option: d.option ?? '', stage: d.stage, currency: d.currency,
      expected_share: d.expected_share?.toString() ?? '', share_max: d.share_max?.toString() ?? '', notes: d.notes ?? '' } : blank);
  };
  const exp = num(f.expected_share);
  const max = num(f.share_max);
  const valid = f.name.trim() && (exp === null || ok(exp)) && (max === null || (ok(max) && (exp === null || max >= exp)));
  return (
    <Modal title="Update a deal" kicker="Pipeline: expected, not counted" onClose={() => onClose(false)}
      footer={<><Dismiss>Cancel</Dismiss>
        <button className="cta" disabled={!valid || busy} onClick={() => run(() => repo.saveMwDeal({
          ...(f.id ? { id: f.id } : {}), name: f.name.trim(), program: f.program.trim() || null, option: f.option.trim() || null,
          stage: f.stage, currency: f.currency, expected_share: exp, share_max: max, notes: f.notes.trim() || null,
        }))}>Save</button></>}>
      <div className="mw-form">
        <Field label="Deal"><select value={f.id} onChange={(e) => pick(e.target.value)}>
          <option value="">New deal</option>{data.deals.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}</select></Field>
        <Field label="Name"><input value={f.name} onChange={(e) => set({ ...f, name: e.target.value })} /></Field>
        <Field label="Program"><input value={f.program} placeholder="Dominica" onChange={(e) => set({ ...f, program: e.target.value })} /></Field>
        <Field label="Option"><input value={f.option} placeholder="Donation" onChange={(e) => set({ ...f, option: e.target.value })} /></Field>
        <Field label="Stage"><select value={f.stage} onChange={(e) => set({ ...f, stage: e.target.value as MwStage })}>
          {(Object.keys(STAGE) as MwStage[]).map((k) => <option key={k} value={k}>{STAGE[k]}</option>)}</select></Field>
        <Field label="Currency"><select value={f.currency} onChange={(e) => set({ ...f, currency: e.target.value as Ccy })}><option>USD</option><option>EUR</option><option>NGN</option></select></Field>
        <Field label="Your expected share"><input inputMode="decimal" value={f.expected_share} onChange={(e) => set({ ...f, expected_share: e.target.value })} /></Field>
        <Field label="Up to (if a range)"><input inputMode="decimal" value={f.share_max} onChange={(e) => set({ ...f, share_max: e.target.value })} /></Field>
        <Field label="Notes"><input value={f.notes} onChange={(e) => set({ ...f, notes: e.target.value })} /></Field>
      </div>
      {err && <ErrorNote message={err} />}
    </Modal>
  );
}

function RatesForm({ data, onClose }: { data: MwData; onClose: (saved: boolean) => void }) {
  const { repo } = useApp();
  const { busy, err, run } = useSave(onClose);
  const [usd, setUsd] = useState(data.settings?.usd_rate?.toString() ?? '');
  const [eur, setEur] = useState(data.settings?.eur_rate?.toString() ?? '');
  const u = num(usd), e = num(eur);
  const valid = (u === null || ok(u)) && (e === null || ok(e)) && Boolean(data.settings);
  return (
    <Modal title="Planning rates" kicker="Used only for expected deal shares" onClose={() => onClose(false)}
      footer={<><Dismiss>Cancel</Dismiss>
        <button className="cta" disabled={!valid || busy} onClick={() => run(() => repo.saveMwRates(u, e))}>Save</button></>}>
      <div className="mw-form">
        <Field label="₦ per $"><input inputMode="decimal" value={usd} onChange={(x) => setUsd(x.target.value)} /></Field>
        <Field label="₦ per €"><input inputMode="decimal" value={eur} onChange={(x) => setEur(x.target.value)} /></Field>
      </div>
      {err && <ErrorNote message={err} />}
    </Modal>
  );
}
